import React, { useState, useEffect, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';

// Define the styled components
const CarouselContainer = styled.div`
  position: relative;
  width: 280px;
  height: 570px;
  margin: 0 auto;
  perspective: 1000px;
`;

const PhoneFrame = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #1a1a1a;
  border-radius: 40px;
  padding: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;
    height: 30px;
    background-color: #1a1a1a;
    border-bottom-left-radius: 18px;
    border-bottom-right-radius: 18px;
    z-index: 2;
  }
`;

const ScreenContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 30px;
  background-color: #000;
`;

// Updated Screen component with better transitions
interface ScreenProps {
  isActive: boolean;
  isNext: boolean;
  transitioning: boolean;
}

const Screen = styled.img<ScreenProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  backface-visibility: hidden;
  will-change: transform, opacity;

  ${({ isActive, isNext, transitioning }) => {
    if (!transitioning) {
      return css`
        opacity: ${isActive ? 1 : 0};
        transform: translateX(0);
        transition: opacity 0.3s ease;
      `;
    }

    if (isActive) {
      return css`
        z-index: 0;
        opacity: 1;
        transform: translateX(0);
        animation: ${keyframes`
          from { transform: translateX(0); }
          to { transform: translateX(-100%); }
        `} 0.5s cubic-bezier(0.32, 0, 0.67, 0) forwards;
      `;
    }

    if (isNext) {
      return css`
        z-index: 1;
        opacity: 1;
        animation: ${keyframes`
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        `} 0.5s cubic-bezier(0.33, 1, 0.68, 1) forwards;
      `;
    }

    return css`
      opacity: 0;
      transform: translateX(-100%);
    `;
  }}
`;

const PhoneCarousel: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(1);
  const [transitioning, setTransitioning] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const screenshots = [
    '/screenshot1.PNG',
    '/screenshot2.PNG',
    '/screenshot3.PNG',
    '/screenshot4.PNG',
    '/screenshot5.PNG',
  ];

  const transition = () => {
    // Start transition
    setTransitioning(true);
    setNextIndex((currentIndex + 1) % screenshots.length);

    // After animation completes, update the current index
    timerRef.current = setTimeout(() => {
      setCurrentIndex((currentIndex + 1) % screenshots.length);
      setTransitioning(false);
    }, 500); // Match animation duration
  };

  useEffect(() => {
    // Set up the interval
    const interval = setInterval(transition, 5000);

    return () => {
      clearInterval(interval);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentIndex]);

  return (
    <CarouselContainer>
      <PhoneFrame>
        <ScreenContainer>
          {screenshots.map((src, index) => (
            <Screen
              key={index}
              src={src}
              isActive={index === currentIndex}
              isNext={index === nextIndex && transitioning}
              transitioning={transitioning}
              alt={`App screenshot ${index + 1}`}
            />
          ))}
        </ScreenContainer>
      </PhoneFrame>
    </CarouselContainer>
  );
};

export default PhoneCarousel;
